/** Accessible tag group. Wraps React Aria TagGroup, TagList, and Tag. */
import {
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
  Tag as AriaTag,
  Label,
  type TagGroupProps as AriaTagGroupProps,
  type TagProps as AriaTagProps,
} from 'react-aria-components';
import type { ReactNode } from 'react';
import { cn } from '@/foundation/utils/cn';
import { X } from 'lucide-react';

interface TagGroupProps<T extends object> extends Omit<AriaTagGroupProps, 'children'> {
  label?: string;
  items?: Iterable<T>;
  children: ReactNode | ((item: T) => ReactNode);
  className?: string;
  renderEmptyState?: () => ReactNode;
}

function TagGroup<T extends object>({
  label,
  items,
  children,
  className,
  renderEmptyState,
  ...props
}: TagGroupProps<T>) {
  return (
    <AriaTagGroup {...props} className={cn('flex flex-col gap-2', className)}>
      {label && (
        <Label className="text-[length:var(--f-text-sm)] font-medium text-[color:var(--f-fg)]">
          {label}
        </Label>
      )}
      <AriaTagList
        items={items}
        renderEmptyState={renderEmptyState}
        className="flex flex-wrap gap-1.5"
      >
        {children}
      </AriaTagList>
    </AriaTagGroup>
  );
}

interface TagProps extends Omit<AriaTagProps, 'children'> {
  children: ReactNode;
  className?: string;
}

function Tag({ children, className, ...props }: TagProps) {
  return (
    <AriaTag
      {...props}
      className={cn(
        'group flex items-center gap-1 px-2.5 py-1 rounded-full text-[length:var(--f-text-xs)] font-medium',
        'bg-[color:var(--f-bg-sunken)] text-[color:var(--f-fg-secondary)]',
        'border border-[color:var(--f-border)]',
        'outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--f-accent)]',
        'transition-colors',
        className
      )}
    >
      {({ allowsRemoving }) => (
        <>
          {children}
          {allowsRemoving && (
            <button
              slot="remove"
              className="ml-0.5 p-0.5 rounded-full hover:bg-[color:var(--f-border)] transition-colors"
            >
              <X size={10} />
            </button>
          )}
        </>
      )}
    </AriaTag>
  );
}

export { TagGroup, Tag };
export type { TagGroupProps, TagProps };
